import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "@components/button/Button";
import { Input } from "@components/form/Input";
import { routes } from "../routes";
import useStorage from "@utils/UseStorage";
import { isCustomerUser } from "@utils/utils";
import { apiPost } from "@api/client";
import { useAuth } from "../auth/AuthContext";
import { REFRESH_TOKEN_KEY, USER_KEY } from "../auth/session";
import { tenant } from "@/theme/tenant";

const LoginPage = () => {
	const navigate = useNavigate();
	const { setItem } = useStorage();
	const { setToken } = useAuth();
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	const ERROR_MSG = "Ogiltigt användarnamn eller lösenord";

	async function handleSubmit() {
		setLoading(true);
		setErrorMsg("");

		try {
			const res = await apiPost<any>("/auth/token", {
				username,
				password,
			});

			if (res) {
				// The API returns { status, data: { accessToken, refreshToken, user } }
				// Our response interceptor automatically unwraps to just { accessToken, refreshToken, user }
				const { accessToken, refreshToken, user } = res;

				if (user && isCustomerUser(user)) {
					// Store user data in localStorage for backward compatibility
					setItem(USER_KEY, JSON.stringify(user));

					// Store the access token in AuthContext and localStorage
					setToken(accessToken);

					// TODO: Consider storing refreshToken for token refresh functionality
					if (refreshToken) {
						localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
					}

					// Navigate to start page
					navigate(routes.start);
				} else {
					setErrorMsg(ERROR_MSG);
				}
				setLoading(false);
			}
		} catch (err) {
			setLoading(false);
			setErrorMsg(ERROR_MSG);
		}
	}

	return (
		<Styles>
			<div className="col-1">
				<h1>
					<img src={tenant.logo} alt={tenant.name} width="170" />
				</h1>

				<Input
					onChangeText={(val) => {
						setUsername(val);
						setErrorMsg("");
					}}
					value={username}
					placeholder="namn@exempel.se"
					label="E-post"
					onDark
					onEnter={handleSubmit}
					className="row"
				/>
				<Input
					onChangeText={(val) => {
						setPassword(val);
						setErrorMsg("");
					}}
					value={password}
					placeholder="Lösenord"
					label="Lösenord"
					type="password"
					onDark
					onEnter={handleSubmit}
					className="row"
				/>
				{errorMsg && <p className="error">{errorMsg}</p>}

				<div className="row">
					<Button
						onClick={handleSubmit}
						disabled={loading}
						type="secondary"
						className="button"
						title="Logga in"
						loadingTitle="Loggar in..."
					/>
				</div>

				<Link to="/aterstall-losenord" className="forgot-password small">
					Problem med att logga in?
				</Link>

				<p className="x-small">Origo Works 1.0.0</p>
			</div>
			{tenant.loginBackgroundImage && (
				<div
					className="col-2"
					style={{ backgroundImage: `url(${tenant.loginBackgroundImage})` }}
				></div>
			)}
		</Styles>
	);
};

export default LoginPage;

const Styles = styled.div`
	min-height: 100vh;
	display: flex;

	.col-1 {
		background-color: ${(props) => props.theme.colors.brand};
		width: 35vw;
		padding: 10vh 1.5rem 1.5rem 1.5rem;
		max-width: 420px;
		display: flex;
		flex-direction: column;

		.error {
			color: ${(props) => props.theme.colors.lightYellow};
			font-style: italic;
			margin: 0;
			height: 25px;
		}

		@media (max-width: 800px) {
			max-width: 100%;
			width: 100%;
			align-items: center;

			.row {
				width: 350px;
				margin: 10px;
			}
		}

		@media (max-width: 420px) {
			.row {
				width: 300px;
				max-width: 100%;
			}
		}
	}

	.col-2 {
		flex: 1;
		background-repeat: no-repeat;
		background-size: cover;

		@media (max-width: 800px) {
			background: none;
			display: none;
			flex: none;
		}
	}

	h1 {
		text-align: center;
		margin-bottom: 3rem;

		/* Tailwind's preflight makes img a block, so text-align doesn't center it */
		img {
			margin: 0 auto;
		}
	}

	.button {
		margin: 2rem auto 0 auto;
		width: 90%;
	}

	p {
		text-align: center;
		color: white;
		opacity: 0.7;
	}

	.forgot-password {
		margin: 1rem;
		text-align: center;
		color: white;
	}
`;
